import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Users, ListOrdered } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ResourceCreditDisplay } from "@/components/credits/ResourceCreditDisplay";
import { PresenceAvatars } from "@/components/presence/PresenceAvatars";
import { normalizeTurnMode, isStructuredTurnMode } from "@/lib/normalize-turn-mode";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";
import { useIsMobile } from "@/hooks/use-mobile";

interface PlayAdminBarProps {
  isWorldOwner: boolean;
  worldId: string | null;
  adventureId: string | null;
  adventureName: string | null;
  isMultiplayer: boolean;
  turnMode: string;
  currentTurnPlayer: string | null;
  isMyTurn: boolean;
  otherUsers: any[];
  designIssuesCount?: number;
}

const MODE_LABELS: Record<string, string> = {
  free_form: "Free-form",
  round_robin: "Round Robin",
  initiative_order: "Initiative",
  concurrent: "Concurrent",
};

export function PlayAdminBar({
  isWorldOwner, worldId, adventureId, adventureName,
  isMultiplayer, turnMode, currentTurnPlayer, isMyTurn, otherUsers,
  designIssuesCount = 0,
}: PlayAdminBarProps) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const mode = normalizeTurnMode(turnMode);
  const structured = isStructuredTurnMode(mode);
  const modeLabel = MODE_LABELS[mode] || mode;

  return (
    <div className="border-b border-border px-2 sm:px-4 py-1 sm:py-1.5 flex items-center gap-1.5 sm:gap-3 bg-card/50 min-h-[32px] sm:min-h-[36px] overflow-hidden max-w-full">
      {isWorldOwner && worldId && !adventureId && (
        <Button variant="ghost" size="sm" className="h-6 sm:h-7 px-1.5 sm:px-2 text-[10px] sm:text-xs gap-1 text-muted-foreground hover:text-foreground relative shrink-0" onClick={() => navigate(`/world/${worldId}`)}>
          <Pencil className="w-3 h-3" />
          {!isMobile && t.play_edit_template}
        </Button>
      )}

      {(isMultiplayer || mode !== "free_form") && (
        <>
          {isMultiplayer && (
            <Badge variant="secondary" className="gap-1 h-5 text-[10px] shrink-0"><Users className="w-3 h-3" />{!isMobile && t.play_multiplayer}</Badge>
          )}
          <Badge variant="outline" className="text-[10px] h-5 gap-1 shrink-0">
            <ListOrdered className="w-3 h-3" />
            {modeLabel}
          </Badge>
          {currentTurnPlayer && structured && (
            <Badge variant={isMyTurn ? "default" : "outline"} className="text-[10px] h-5 gap-1 shrink-0">
              {isMyTurn ? t.play_your_turn_badge : tFormat(t.play_other_turn, { name: currentTurnPlayer })}
            </Badge>
          )}
          {structured && !isMyTurn && !currentTurnPlayer && (
            <span className="text-xs text-muted-foreground ml-auto">{t.play_waiting_other}</span>
          )}
        </>
      )}

      {adventureName && !isMobile && (
        <span className="text-xs text-muted-foreground truncate ml-auto">{adventureName}</span>
      )}
      <div className="ml-auto shrink-0">
        <ResourceCreditDisplay resourceType="adventure" resourceId={adventureId || null} />
      </div>
      {otherUsers.length > 0 && (
        <div className="shrink-0">
          <PresenceAvatars users={otherUsers} size="sm" />
        </div>
      )}
    </div>
  );
}
